import { useMemo } from 'react'
import { type ColumnDef } from '@tanstack/react-table'
import { Flame, ShieldCheck } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { AvatarGroup, SourceGlyph, TypeGlyph } from '@/components/common/badges'
import { IssueLink } from '@/components/common/issue-link'
import { LinkedPrIcons } from '@/components/common/linked-pr-icons'
import { IssueDataTable } from '@/components/tables/data-table'
import { useVerifyAffordances } from '@/hooks/use-verify-affordances'
import { daysAtReleased } from '@/lib/computed'
import { cn } from '@/lib/utils'
import type { WorkIssue } from '@/types/github'

const HOT_RELEASED_DAYS = 10

/** Inline verify action for the queue — prefers the prod close (the action that
 *  finishes the issue) when both apply; renders nothing for non-verifiers. */
function VerifyCell({ issue }: { issue: WorkIssue }) {
  const { canVerifyQa, canVerifyProd, verifyQa, close } = useVerifyAffordances(issue)
  if (!canVerifyProd && !canVerifyQa) return null

  const onProd = canVerifyProd
  return (
    <Button
      size="sm"
      variant="outline"
      className="h-6 text-xs"
      disabled={onProd ? close.isPending : verifyQa.isPending || verifyQa.isSuccess}
      onClick={(e) => {
        e.stopPropagation()
        if (onProd) close.mutate({ issue })
        else verifyQa.mutate({ issue })
      }}
    >
      <ShieldCheck className="size-3" />
      {onProd ? 'Verify & close' : 'Verify in QA'}
    </Button>
  )
}

export function VerifyTable({
  issues,
  dateOf,
  dateHeader,
}: {
  issues: WorkIssue[]
  /** Which release date drives the queue (QA vs Prod gate). */
  dateOf: (issue: WorkIssue) => string | null
  dateHeader: string
}) {
  const columns = useMemo<ColumnDef<WorkIssue>[]>(
    () => [
      {
        id: 'issue',
        accessorFn: (row) => row.number,
        header: 'Issue',
        cell: ({ row }) => (
          <IssueLink
            number={row.original.number}
            className="flex min-w-0 items-center gap-2 hover:underline focus-visible:underline"
          >
            <span className="text-faint-foreground w-11 shrink-0 text-right font-mono text-[11px] tabular-nums">
              #{row.original.number}
            </span>
            <TypeGlyph typeName={row.original.typeName} />
            <span className="max-w-90 truncate font-medium">{row.original.title}</span>
          </IssueLink>
        ),
      },
      {
        id: 'product',
        accessorFn: (row) => row.product ?? '',
        header: 'Product',
        cell: ({ row }) => <span className="text-muted-foreground text-xs">{row.original.product}</span>,
      },
      {
        id: 'client',
        accessorFn: (row) => row.client ?? '',
        header: 'Client',
        cell: ({ row }) =>
          row.original.client ? (
            <Badge variant="outline" className="px-1.5 py-px font-mono text-[10.5px] font-normal">
              {row.original.client}
            </Badge>
          ) : null,
      },
      {
        id: 'releasedAt',
        accessorFn: (row) => dateOf(row) ?? '',
        header: dateHeader,
        cell: ({ row }) => (
          <span className="text-muted-foreground font-mono text-[11px]">
            {dateOf(row.original) ?? '—'}
          </span>
        ),
      },
      {
        id: 'daysReleased',
        accessorFn: (row) => daysAtReleased(dateOf(row)) ?? -1,
        header: `Days at ${dateHeader.replace('Released to ', '')}`,
        cell: ({ getValue }) => {
          const days = getValue<number>()
          if (days < 0) return <span className="text-faint-foreground">—</span>
          const hot = days >= HOT_RELEASED_DAYS
          return (
            <span
              className={cn(
                'inline-flex items-center gap-1 font-mono text-[11px]',
                hot ? 'text-destructive font-medium' : 'text-muted-foreground',
              )}
            >
              {hot && <Flame className="size-3" />}
              {days}d
            </span>
          )
        },
      },
      {
        id: 'assignees',
        header: 'Assignees',
        enableSorting: false,
        cell: ({ row }) => <AvatarGroup users={row.original.assignees} />,
      },
      {
        id: 'source',
        accessorFn: (row) => row.source ?? '',
        header: 'Source',
        cell: ({ row }) => <SourceGlyph source={row.original.source} />,
      },
      {
        id: 'prs',
        header: 'PR',
        enableSorting: false,
        cell: ({ row }) => <LinkedPrIcons prs={row.original.linkedPrs} />,
      },
      {
        id: 'verify',
        header: '',
        enableSorting: false,
        cell: ({ row }) => <VerifyCell issue={row.original} />,
      },
    ],
    [dateOf, dateHeader],
  )

  return (
    <IssueDataTable
      issues={issues}
      columns={columns}
      sortable
      // Oldest releases first — that's the verifier queue's whole point.
      initialSorting={[{ id: 'daysReleased', desc: true }]}
      bulkKind="work"
      emptyTitle="Nothing awaiting verification."
    />
  )
}
